import React, { useEffect, useState } from 'react';
import {
  ChevronRight, ChevronLeft,
  Download, Loader2,
  Check, Globe, Copy, Zap,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import { agentService, deploymentService } from '../services/api';
import { Agent } from '../types';
import toast from 'react-hot-toast';
import axios from 'axios';
import { ADMIN_KEY } from '../config/api';

const API = process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in';

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type ClientMode = 'app' | 'mcp' | null;
type Template = { id: string; name: string; description: string; icon: string; features: string[] };

const DeploymentStudioPage: React.FC = () => {
  const [step, setStep] = useState<Step>(1);
  const [clientMode, setClientMode] = useState<ClientMode>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);

  // MCP client state
  const [mcpClientInfo, setMcpClientInfo] = useState({ name: '', email: '', project: '' });
  const [mcpInitialTokens, setMcpInitialTokens] = useState('500');
  const [mcpBuilding, setMcpBuilding] = useState(false);
  const [mcpResult, setMcpResult] = useState<{ apiKey: string; clientId: string; assignedAgents: string[] } | null>(null);

  // Selections
  const [selectedTemplate, setSelectedTemplate] = useState('dashboard');
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [dbEnabled, setDbEnabled] = useState(false);
  const [branding, setBranding] = useState({ primaryColor: '#6366f1', secondaryColor: '#8b5cf6', logo: '⚖️', appName: '', tagline: 'Legal AI Assistant' });
  const [clientInfo, setClientInfo] = useState({ name: '', email: '', phone: '', firm: '', address: '' });

  // Build
  const [building, setBuilding] = useState(false);
  const [buildDone, setBuildDone] = useState<{ url: string; name: string; buildId: string; clientId?: string; credentials?: { username: string; password: string }; cloudUrl?: string; cloudCname?: { record: string; instructions: string; example: string }; cloudSsl?: string } | null>(null);

  // Agreement
  const [tokenCost, setTokenCost] = useState('50');
  const [deploymentCost, setDeploymentCost] = useState('0');
  const [initialTokens, setInitialTokens] = useState('100');
  const [agreement, setAgreement] = useState('');
  const [generatingAgreement, setGeneratingAgreement] = useState(false);

  // Preview key to force iframe reload
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([
          agentService.getAll(),
          axios.get(`${API}/api/templates`).then(r => r.data.templates),
        ]);
        setAgents(a);
        setTemplates(t);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const selectedAgents = agents.filter(a => selectedAgentIds.includes(a.id));
  const hasVoice = selectedAgents.some(a => (a.capabilities || []).some(c => c.toLowerCase().includes('voice')));
  const hasOcr = selectedAgents.some(a => (a.capabilities || []).some(c => c.toLowerCase().includes('ocr')));

  // ── MCP Client Creation ──
  const handleCreateMcpClient = async () => {
    if (!mcpClientInfo.name || !mcpClientInfo.email) { toast.error('Name and email required'); return; }
    if (selectedAgentIds.length === 0) { toast.error('Select at least one agent'); return; }
    setMcpBuilding(true);
    setMcpResult(null);
    try {
      // 1. Create client
      const client = await deploymentService.createClient({
        name: mcpClientInfo.name,
        email: mcpClientInfo.email,
        phone: '',
        organization: mcpClientInfo.project || mcpClientInfo.name,
      });
      // 2. Assign agents
      await deploymentService.assignAgents(client.id, selectedAgentIds);
      // 3. Set contract with allowServerToServer = true (MCP flag)
      await deploymentService.setContract(client.id, {
        allowServerToServer: true,
        allowedOrigins: [],
        costPerToken: 50,
        billingCycle: 'prepaid',
        notes: `MCP client for Claude/ChatGPT/Gemini — created ${new Date().toISOString()}`,
      });
      // 4. Recharge initial tokens
      if (Number(mcpInitialTokens) > 0) {
        await deploymentService.rechargeTokens(client.id, Number(mcpInitialTokens));
      }
      // 5. Fetch the generated API key
      const clientData = await deploymentService.getClient(client.id);
      setMcpResult({
        apiKey: clientData.apiKey || '',
        clientId: client.id,
        assignedAgents: selectedAgentIds,
      });
      toast.success('MCP client created!');
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Failed to create MCP client');
    } finally {
      setMcpBuilding(false);
    }
  };

  const canNext = () => {
    if (step === 1) return !!selectedTemplate;
    if (step === 2) return selectedAgentIds.length > 0;
    if (step === 3) return true;
    if (step === 4) return branding.appName.trim().length > 0;
    if (step === 5) return true; // preview
    if (step === 6) return agreement.length > 0; // agreement generated
    if (step === 7) return clientInfo.name.trim().length > 0 && clientInfo.email.trim().length > 0;
    return false;
  };

  const previewUrl = `/deployment-preview?agents=${selectedAgentIds.join(',')}&db=${dbEnabled}&appName=${encodeURIComponent(branding.appName || 'Legal AI')}&primary=${encodeURIComponent(branding.primaryColor)}&_k=${previewKey}`;

  const STEPS = [
    { n: 1, label: 'Template', icon: '🎨' },
    { n: 2, label: 'Agent', icon: '🤖' },
    { n: 3, label: 'Database', icon: '📦' },
    { n: 4, label: 'Branding', icon: '🎨' },
    { n: 5, label: 'Client & Tokens', icon: '💰' },
    { n: 6, label: 'Agreement', icon: '📋' },
    { n: 7, label: 'Preview & Build', icon: '🚀' },
  ];

  const handleGenerateAgreement = async () => {
    setGeneratingAgreement(true);
    try {
      const agentNames = selectedAgents.map(a => a.name).join(', ');
      const { data } = await axios.post(`${API}/api/deployment/generate-agreement`, {
        clientName: clientInfo.name || branding.appName || 'Client',
        clientEmail: clientInfo.email || '',
        clientPhone: clientInfo.phone || '',
        clientFirm: clientInfo.firm || branding.appName || '',
        clientAddress: clientInfo.address || '',
        tokenCostPerUnit: tokenCost,
        oneTimeDeploymentCost: deploymentCost,
        initialTokens,
        selectedAgents: agentNames,
        dbEnabled,
      }, {
        headers: { 'x-superadmin-key': ADMIN_KEY },
      });
      if (data.success && data.agreement) {
        setAgreement(data.agreement);
        toast.success('Agreement generated');
      } else {
        toast.error(data.error || 'Failed to generate agreement');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Failed');
    } finally { setGeneratingAgreement(false); }
  };

  const handleDownloadAgreementPdf = () => {
    // Create a printable HTML and trigger print/save as PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Please allow popups'); return; }
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Agreement — ${branding.appName || 'Client'}</title>
<style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:20px;line-height:1.8;color:#1a1a1a;font-size:14px}
h1{text-align:center;font-size:20px;margin-bottom:30px}h2{font-size:16px;margin-top:24px}
.header{text-align:center;margin-bottom:40px;padding-bottom:20px;border-bottom:2px solid #333}
.footer{margin-top:60px;display:flex;justify-content:space-between}
.sig-block{width:45%;border-top:1px solid #333;padding-top:10px;margin-top:40px}
@media print{body{margin:0;padding:20px}}</style></head><body>
<div class="header"><h1>SERVICE AGREEMENT</h1><p>Between ViddhiGuru and ${clientInfo.name || branding.appName || 'Client'}</p></div>
<div style="white-space:pre-wrap">${agreement}</div>
<script>setTimeout(()=>window.print(),500)<\/script></body></html>`);
    printWindow.document.close();
  };

  if (loading) return (
    <div className="flex flex-col h-screen">
      <Header title="Deployment Studio" subtitle="Build legal AI apps" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </div>
  );

  // ── Mode selector — shown before anything else ──
  if (!clientMode) {
    return (
      <div className="flex flex-col h-screen">
        <Header title="Deployment Studio" subtitle="Build & deploy legal AI apps for your clients" />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-center mb-2">What are you building?</h2>
            <p className="text-center text-gray-500 text-sm mb-8">Choose the type of client to create</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Lawyer App */}
              <button onClick={() => setClientMode('app')}
                className="p-6 rounded-2xl border-2 border-gray-200 hover:border-primary hover:bg-primary/5 text-left transition-all group">
                <div className="w-14 h-14 rounded-2xl bg-indigo-100 group-hover:bg-primary flex items-center justify-center text-3xl mb-4 transition-all">🏛️</div>
                <h3 className="font-bold text-lg mb-1">Lawyer App</h3>
                <p className="text-sm text-gray-500 mb-4">Full standalone application for a lawyer, firm, or institution. Includes AI chat, voice, OCR, case management, and billing.</p>
                <div className="space-y-1 text-xs text-gray-600">
                  {['Complete PWA app with login', 'Case & client management', 'White-label branding', 'Domain-restricted API key'].map(f => (
                    <div key={f} className="flex items-center gap-1.5"><Check size={10} className="text-green-500" />{f}</div>
                  ))}
                </div>
              </button>
              {/* MCP / API Client */}
              <button onClick={() => setClientMode('mcp')}
                className="p-6 rounded-2xl border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50 text-left transition-all group">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 group-hover:bg-amber-400 flex items-center justify-center text-3xl mb-4 transition-all">⚡</div>
                <h3 className="font-bold text-lg mb-1">MCP / API Client</h3>
                <p className="text-sm text-gray-500 mb-4">API key for Claude, ChatGPT, Gemini integrations. Server-to-server access — no browser, no domain restriction. Key-only auth.</p>
                <div className="space-y-1 text-xs text-gray-600">
                  {['API key + MCP config', 'Claude, ChatGPT, Gemini ready', 'Token billing enforced', 'No app deployment needed'].map(f => (
                    <div key={f} className="flex items-center gap-1.5"><Check size={10} className="text-amber-500" />{f}</div>
                  ))}
                </div>
                <div className="mt-3 px-2 py-1 bg-amber-100 rounded-lg text-[10px] text-amber-700 font-medium">
                  ⚠️ allowServerToServer = true — key-only auth, no domain check
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MCP Client Flow ──
  if (clientMode === 'mcp') {
    return (
      <div className="flex flex-col h-screen">
        <Header title="Deployment Studio" subtitle="MCP / API Client Setup" />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-6">
            <button onClick={() => { setClientMode(null); setMcpResult(null); setSelectedAgentIds([]); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6">
              <ChevronLeft size={16} /> Back to type selection
            </button>

            {!mcpResult ? (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold mb-1">⚡ Create MCP / API Client</h2>
                  <p className="text-sm text-gray-500">Creates a server-to-server API key for Claude, ChatGPT, or Gemini. No app deployment — key-only access.</p>
                </div>

                {/* Client info */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Client Details</h3>
                  <div className="space-y-3">
                    <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Name *</label>
                      <input value={mcpClientInfo.name} onChange={e => setMcpClientInfo(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400" placeholder="e.g. Bar Council Maharashtra" /></div>
                    <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Email *</label>
                      <input value={mcpClientInfo.email} onChange={e => setMcpClientInfo(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400" placeholder="contact@client.com" /></div>
                    <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Project / Integration Name</label>
                      <input value={mcpClientInfo.project} onChange={e => setMcpClientInfo(p => ({ ...p, project: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400" placeholder="e.g. Claude Legal Assistant" /></div>
                  </div>
                </div>

                {/* Agent selection */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">Select Agents</h3>
                  <p className="text-xs text-gray-400 mb-3">Which legal AI agents should this key have access to?</p>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {agents.map(a => {
                      const on = selectedAgentIds.includes(a.id);
                      return (
                        <div key={a.id} onClick={() => setSelectedAgentIds(p => on ? p.filter(x => x !== a.id) : [...p, a.id])}
                          className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center gap-2 ${on ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <span className="text-lg">{a.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold truncate">{a.name}</div>
                            <div className="text-[9px] text-gray-400 truncate">{a.description || a.id}</div>
                          </div>
                          {on && <Check size={12} className="text-amber-500 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Token pack */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Initial Token Balance</h3>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[['100', '₹250', 'Testing'], ['500', '₹1,000', 'Developer'], ['2000', '₹3,500', 'Pro'], ['10000', '₹15,000', 'Scale']].map(([tokens, price, label]) => (
                      <button key={tokens} onClick={() => setMcpInitialTokens(tokens)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${mcpInitialTokens === tokens ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}>
                        <div className="text-sm font-bold">{Number(tokens).toLocaleString()}</div>
                        <div className="text-[9px] text-gray-500">{price}</div>
                        <div className="text-[8px] text-amber-600 font-medium">{label}</div>
                      </button>
                    ))}
                  </div>
                  <input type="number" value={mcpInitialTokens} onChange={e => setMcpInitialTokens(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400" placeholder="Custom token amount" />
                </div>

                <button onClick={handleCreateMcpClient} disabled={mcpBuilding || !mcpClientInfo.name || !mcpClientInfo.email || selectedAgentIds.length === 0}
                  className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl font-bold text-sm hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  {mcpBuilding ? <><Loader2 size={16} className="animate-spin" /> Creating MCP client...</> : <><Zap size={16} /> Create MCP Client & Generate API Key</>}
                </button>
              </div>
            ) : (
              /* ── MCP Success Screen ── */
              <div className="space-y-5">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <Check size={20} className="text-green-600" />
                  <div>
                    <div className="font-bold text-green-800 text-sm">MCP Client Created</div>
                    <div className="text-xs text-green-600">{mcpClientInfo.name} — {mcpResult.clientId}</div>
                  </div>
                </div>

                {/* API Key */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">🔑 API Key</h3>
                  <div className="flex items-center gap-2 bg-gray-900 rounded-xl p-3">
                    <code className="flex-1 text-green-400 text-xs font-mono break-all">{mcpResult.apiKey}</code>
                    <button onClick={() => { navigator.clipboard.writeText(mcpResult.apiKey); toast.success('Copied!'); }}
                      className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all shrink-0">
                      <Copy size={14} className="text-gray-300" />
                    </button>
                  </div>
                  <p className="text-[10px] text-red-600 mt-2">⚠️ Save this key. It won't be shown again in full. Manage from Client Management.</p>
                </div>

                {/* Claude MCP Config */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">🤖 Claude / Kiro MCP Config</h3>
                  <p className="text-xs text-gray-400 mb-3">Add to your <code className="bg-gray-100 px-1 rounded">mcp.json</code> or Claude Desktop settings</p>
                  {(() => {
                    const config = JSON.stringify({
                      mcpServers: {
                        "joilaw-legal-ai": {
                          command: "npx",
                          args: ["@joilaw/mcp-server@latest"],
                          env: {
                            JOILAW_API_KEY: mcpResult.apiKey,
                            JOILAW_API_URL: API,
                          }
                        }
                      }
                    }, null, 2);
                    return (
                      <>
                        <pre className="bg-gray-900 text-green-400 rounded-xl p-4 text-xs font-mono overflow-x-auto whitespace-pre">{config}</pre>
                        <button onClick={() => { navigator.clipboard.writeText(config); toast.success('Config copied!'); }}
                          className="mt-2 w-full py-2 border border-gray-200 rounded-xl text-xs text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-1.5">
                          <Copy size={12} /> Copy MCP Config
                        </button>
                      </>
                    );
                  })()}
                </div>

                {/* ChatGPT Actions */}
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-1">💬 ChatGPT Actions / Gemini</h3>
                  <p className="text-xs text-gray-400 mb-3">Use these REST endpoints directly in ChatGPT Actions or Gemini Function Calling</p>
                  <div className="space-y-2 text-xs font-mono">
                    {[
                      { label: 'Legal Query', method: 'POST', path: `/api/v1/agents/${mcpResult.assignedAgents[0] || '{agentId}'}/chat` },
                      { label: 'Pro Analysis', method: 'POST', path: `/api/v1/agents/${mcpResult.assignedAgents[0] || '{agentId}'}/chat/pro` },
                      { label: 'List Agents', method: 'GET', path: '/api/v1/agents' },
                      { label: 'Check Balance', method: 'GET', path: '/api/v1/agents/usage' },
                    ].map(ep => (
                      <div key={ep.path} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ep.method === 'POST' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{ep.method}</span>
                        <span className="text-gray-600 flex-1 truncate">{API}{ep.path}</span>
                        <span className="text-gray-400 text-[9px]">{ep.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-2 bg-gray-100 rounded-lg">
                    <div className="text-[10px] text-gray-600">Auth header for all requests:</div>
                    <code className="text-[10px] text-primary font-mono">x-api-key: {mcpResult.apiKey.slice(0, 16)}...</code>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => { setClientMode(null); setMcpResult(null); setMcpClientInfo({ name: '', email: '', project: '' }); setSelectedAgentIds([]); }}
                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">
                    Create Another
                  </button>
                  <button onClick={() => window.open('/client-management', '_self')}
                    className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90">
                    Manage in Client Management →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Lawyer App flow (existing 7-step wizard) ──
  return (
    <div className="flex flex-col h-screen">
      <Header title="Deployment Studio" subtitle="Build & deploy legal AI apps for your clients" />
      <div className="flex-1 overflow-y-auto">
        {/* Progress */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="flex items-center gap-1 max-w-4xl mx-auto">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && <div className={`flex-1 h-0.5 ${step >= s.n ? 'bg-primary' : 'bg-gray-200'}`} />}
                <button onClick={() => s.n <= step && setStep(s.n as Step)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
                    step === s.n ? 'bg-primary text-white shadow-md' : step > s.n ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'
                  }`}>
                  {step > s.n ? <Check size={10} /> : <span>{s.icon}</span>}
                  <span className="hidden md:inline">{s.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          {/* Step 1: Template */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Choose Interface Template</h2>
              <p className="text-sm text-gray-500 mb-6">Select the UI layout for your client's application</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {templates.map(t => {
                  const isDisabled = t.id === 'modern-chat' || t.id === 'minimal-widget';
                  return (
                    <div key={t.id} onClick={() => !isDisabled && setSelectedTemplate(t.id)}
                      className={`p-5 rounded-2xl border-2 transition-all relative ${
                        isDisabled
                          ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                          : selectedTemplate === t.id
                            ? 'border-primary bg-primary/5 shadow-lg cursor-pointer'
                            : 'border-gray-200 hover:border-primary/30 cursor-pointer'
                      }`}>
                      {isDisabled && (
                        <div className="absolute top-3 right-3 px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded-full uppercase tracking-wide">
                          Coming Soon
                        </div>
                      )}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-3 ${
                        isDisabled ? 'bg-gray-200 text-gray-400' : selectedTemplate === t.id ? 'bg-primary text-white' : 'bg-gray-100'
                      }`}>{t.icon}</div>
                      <h3 className="text-sm font-bold text-gray-900 mb-1">{t.name}</h3>
                      <p className="text-xs text-gray-500 mb-3">{t.description}</p>
                      <div className="space-y-1">
                        {t.features.map((f, i) => <div key={i} className="text-[10px] text-gray-600 flex items-center gap-1"><Check size={8} className={isDisabled ? 'text-gray-300' : 'text-green-500'} /> {f}</div>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Agent */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Select Agent(s)</h2>
              <p className="text-sm text-gray-500 mb-1">Choose which AI agents to include</p>
              <p className="text-xs text-amber-600 mb-4">⚠️ Maximum 2 agents per client • Selected: {selectedAgentIds.length}/2</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {agents.map(a => {
                  const on = selectedAgentIds.includes(a.id);
                  const v = (a.capabilities||[]).some(c => c.toLowerCase().includes('voice'));
                  const o = (a.capabilities||[]).some(c => c.toLowerCase().includes('ocr'));
                  const atLimit = selectedAgentIds.length >= 2 && !on;
                  return (
                    <div key={a.id} onClick={() => {
                      if (atLimit) { toast.error('Maximum 2 agents per client'); return; }
                      setSelectedAgentIds(p => on ? p.filter(x => x !== a.id) : [...p, a.id]);
                    }}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${on ? 'border-green-400 bg-green-50' : atLimit ? 'border-gray-100 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-lg">{a.icon}</div>
                        <div className="flex-1"><div className="font-semibold text-sm">{a.name}</div><div className="text-[10px] text-gray-500 truncate">{a.description}</div></div>
                        {on && <Check size={18} className="text-green-500" />}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(a.capabilities||[]).slice(0, 4).map((c, i) => <span key={i} className="text-[8px] px-1.5 py-0.5 bg-white border border-gray-200 rounded-full text-gray-500">{c}</span>)}
                        {v && <span className="text-[8px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">🎤 Voice</span>}
                        {o && <span className="text-[8px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">📄 OCR</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Database */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Database & Storage</h2>
              <p className="text-sm text-gray-500 mb-6">Enable in-built storage for clients and cases</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div onClick={() => setDbEnabled(false)} className={`p-6 rounded-2xl border-2 cursor-pointer ${!dbEnabled ? 'border-primary bg-primary/5 shadow-lg' : 'border-gray-200'}`}>
                  <div className="text-3xl mb-3">💬</div><h3 className="font-bold mb-1">No Database</h3><p className="text-xs text-gray-500">Chat only — no data saved</p>
                </div>
                <div onClick={() => setDbEnabled(true)} className={`p-6 rounded-2xl border-2 cursor-pointer ${dbEnabled ? 'border-green-400 bg-green-50 shadow-lg' : 'border-gray-200'}`}>
                  <div className="text-3xl mb-3">📦</div><h3 className="font-bold mb-1">In-built Storage</h3><p className="text-xs text-gray-500 mb-3">Storage for clients, cases & conversations</p>
                  <div className="space-y-1 text-[10px] text-gray-600">
                    {['👥 Users & Auth', '👤 Clients', '📁 Cases', '💬 Conversations', '📄 Documents', '📊 Sessions & Logs'].map((f, i) => <div key={i} className="flex items-center gap-1"><Check size={8} className="text-green-500" /> {f}</div>)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Branding */}
          {step === 4 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Branding</h2>
              <p className="text-sm text-gray-500 mb-6">Customize appearance</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div><label className="text-xs font-semibold text-gray-600 block mb-1">App Name *</label><input value={branding.appName} onChange={e => setBranding(p => ({ ...p, appName: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="My Legal AI" /></div>
                  <div><label className="text-xs font-semibold text-gray-600 block mb-1">Tagline</label><input value={branding.tagline} onChange={e => setBranding(p => ({ ...p, tagline: e.target.value }))} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" /></div>
                  <div><label className="text-xs font-semibold text-gray-600 block mb-1">Logo</label><input value={branding.logo} onChange={e => setBranding(p => ({ ...p, logo: e.target.value }))} className="w-20 px-3 py-2.5 border-2 border-gray-200 rounded-xl text-2xl text-center focus:outline-none focus:border-primary" maxLength={4} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs font-semibold text-gray-600 block mb-1">Primary</label><div className="flex gap-2"><input type="color" value={branding.primaryColor} onChange={e => setBranding(p => ({ ...p, primaryColor: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" /><input value={branding.primaryColor} onChange={e => setBranding(p => ({ ...p, primaryColor: e.target.value }))} className="flex-1 px-2 py-1 border rounded-lg text-xs font-mono" /></div></div>
                    <div><label className="text-xs font-semibold text-gray-600 block mb-1">Secondary</label><div className="flex gap-2"><input type="color" value={branding.secondaryColor} onChange={e => setBranding(p => ({ ...p, secondaryColor: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" /><input value={branding.secondaryColor} onChange={e => setBranding(p => ({ ...p, secondaryColor: e.target.value }))} className="flex-1 px-2 py-1 border rounded-lg text-xs font-mono" /></div></div>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-full max-w-[260px] bg-white rounded-2xl shadow-xl overflow-hidden border">
                    <div className="p-4" style={{ background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.secondaryColor})` }}>
                      <div className="flex items-center gap-3"><div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg">{branding.logo}</div><div><div className="text-sm font-bold text-white">{branding.appName || 'App Name'}</div><div className="text-[9px] text-white/70">{branding.tagline}</div></div></div>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="bg-gray-100 rounded-xl p-2 text-[9px] text-gray-600 max-w-[65%]">Hello! How can I help?</div>
                      <div className="rounded-xl p-2 text-[9px] text-white max-w-[65%] ml-auto" style={{ background: branding.primaryColor }}>What is Section 302?</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Preview */}
          {step === 5 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold mb-1">Live Preview</h2>
                  <p className="text-sm text-gray-500">Test the complete application with real AI responses</p>
                </div>
                <button onClick={() => setPreviewKey(k => k + 1)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50">🔄 Reload</button>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-[10px] px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full border border-indigo-200 font-medium">🎨 {templates.find(t => t.id === selectedTemplate)?.name}</span>
                <span className="text-[10px] px-2.5 py-1 bg-green-50 text-green-700 rounded-full border border-green-200 font-medium">🤖 {selectedAgentIds.length} Agent{selectedAgentIds.length !== 1 ? 's' : ''}</span>
                {hasVoice && <span className="text-[10px] px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-200 font-medium">🎤 Voice</span>}
                {hasOcr && <span className="text-[10px] px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-200 font-medium">📄 OCR</span>}
                {dbEnabled && <span className="text-[10px] px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-200 font-medium">📦 Database</span>}
              </div>
              <div className="border border-gray-300 rounded-xl overflow-hidden shadow-xl">
                <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center gap-3">
                  <div className="flex gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><div className="w-3 h-3 rounded-full bg-yellow-400" /><div className="w-3 h-3 rounded-full bg-green-400" /></div>
                  <div className="flex-1 bg-white rounded-lg px-3 py-1.5 text-xs text-gray-500 font-mono border border-gray-200">🔒 https://{(branding.appName || 'app').toLowerCase().replace(/\s+/g, '-')}.joilaw.ai</div>
                </div>
                <iframe key={previewKey} title="Preview" src={previewUrl} className="w-full bg-white" style={{ height: '600px', border: 'none' }} />
              </div>
              <p className="text-[10px] text-gray-400 text-center mt-3">Fully functional preview — real AI responses from Core API</p>
            </div>
          )}

          {/* Step 6: Agreement */}
          {step === 6 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Client Agreement</h2>
              <p className="text-sm text-gray-500 mb-6">Generate a service agreement between ViddhiGuru and the client</p>
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Client Details</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Client / Firm Name *</label><input value={clientInfo.name} onChange={e => setClientInfo(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Law firm or client name" /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Email *</label><input value={clientInfo.email} onChange={e => setClientInfo(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="client@firm.com" /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Phone</label><input value={clientInfo.phone} onChange={e => setClientInfo(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="+91..." /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Business Name</label><input value={clientInfo.firm} onChange={e => setClientInfo(p => ({ ...p, firm: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Consulting firm" /></div>
                  <div className="col-span-2"><label className="text-[10px] font-semibold text-gray-500 block mb-1">Billing Address</label><input value={clientInfo.address} onChange={e => setClientInfo(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Full billing address" /></div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">💰 Pricing</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 block mb-1">Token Cost (₹ per token)</label>
                    <input type="number" value={tokenCost} onChange={e => setTokenCost(e.target.value)} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" />
                    <p className="text-[9px] text-gray-400 mt-1">Usage-based billing per API call</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 block mb-1">One-time Deployment Cost (₹)</label>
                    <input type="number" value={deploymentCost} onChange={e => setDeploymentCost(e.target.value)} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" />
                    <p className="text-[9px] text-gray-400 mt-1">Setup, customization & configuration</p>
                  </div>
                </div>
              </div>
              <button onClick={handleGenerateAgreement} disabled={generatingAgreement || !clientInfo.name}
                className="w-full py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold text-sm hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 mb-4">
                {generatingAgreement ? <><Loader2 size={16} className="animate-spin" /> Generating agreement with AI...</> : <><span>📋</span> Generate Service Agreement</>}
              </button>
              {agreement && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700">📄 Service Agreement</h3>
                    <div className="flex gap-2">
                      <button onClick={handleDownloadAgreementPdf} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1"><Download size={12} /> Save as PDF</button>
                      <button onClick={handleGenerateAgreement} disabled={generatingAgreement} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200">🔄 Regenerate</button>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-6 max-h-[500px] overflow-y-auto">
                    <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap text-[13px] leading-relaxed font-serif">{agreement}</div>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-700 flex items-center gap-2">
                    <Check size={14} /> Agreement generated. Review terms, download PDF, then proceed to Build.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 7: Build */}
          {step === 7 && (
            <div>
              <h2 className="text-xl font-bold mb-1">Build & Download</h2>
              <p className="text-sm text-gray-500 mb-6">Enter client details, then build a self-contained app</p>

              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Name *</label><input value={clientInfo.name} onChange={e => setClientInfo(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Client name" /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Email *</label><input value={clientInfo.email} onChange={e => setClientInfo(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="client@firm.com" /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Phone</label><input value={clientInfo.phone} onChange={e => setClientInfo(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="+91..." /></div>
                  <div><label className="text-[10px] font-semibold text-gray-500 block mb-1">Firm / Business</label><input value={clientInfo.firm} onChange={e => setClientInfo(p => ({ ...p, firm: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Law firm name" /></div>
                  <div className="col-span-2"><label className="text-[10px] font-semibold text-gray-500 block mb-1">Billing Address</label><input value={clientInfo.address} onChange={e => setClientInfo(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="Full address" /></div>
                </div>
              </div>

              {/* Token & API Assignment */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">⚡ Token & API Assignment</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 block mb-1">Initial Tokens</label>
                    <input type="number" value={initialTokens} onChange={e => setInitialTokens(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" placeholder="100" />
                    <p className="text-[9px] text-gray-400 mt-1">Tokens assigned to client</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 block mb-1">Cost per Token (₹)</label>
                    <input type="number" value={tokenCost} onChange={e => setTokenCost(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" />
                    <p className="text-[9px] text-gray-400 mt-1">Per agreement</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-gray-500 block mb-1">One-time Fee (₹)</label>
                    <input type="number" value={deploymentCost} onChange={e => setDeploymentCost(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary" />
                    <p className="text-[9px] text-gray-400 mt-1">Deployment cost</p>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-amber-700">
                  💡 Each app gets a unique API key. Tokens are deducted per query. When balance reaches 5 tokens, a warning is shown.
                </div>
              </div>

              {/* What gets built */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
                <h4 className="text-xs font-semibold text-indigo-700 mb-2">📦 Your build includes:</h4>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-indigo-600">
                  <div>✅ Self-installing PWA</div>
                  <div>✅ {selectedAgents.map(a => a.name).join(', ')} agent(s)</div>
                  <div>✅ Streaming AI chat</div>
                  <div>✅ Token management</div>
                  {hasVoice && <div>✅ Voice mode</div>}
                  {hasOcr && <div>✅ OCR / Document scan</div>}
                  {dbEnabled && <div>✅ In-built storage for clients & cases</div>}
                  {dbEnabled && <div>✅ Client & Case management</div>}
                  <div>✅ Login authentication</div>
                  <div>✅ Deployment guide</div>
                  <div>✅ Unique API key (per app)</div>
                  <div>✅ {initialTokens} initial tokens</div>
                </div>
              </div>

              <div className="space-y-3">
                <button onClick={async () => {
                  if (!clientInfo.name || !clientInfo.email) { toast.error('Fill client name and email'); return; }
                  setBuilding(true); setBuildDone(null);
                  try {
                    let client;
                    try { client = await deploymentService.createClient({ name: clientInfo.name, email: clientInfo.email, phone: clientInfo.phone, organization: clientInfo.firm }); }
                    catch { const clients = await deploymentService.getClients(); client = clients.find(c => c.email === clientInfo.email); if (!client) throw new Error('Failed to create client'); client = await deploymentService.getClient(client.id); }
                    await deploymentService.assignAgents(client.id, selectedAgentIds);
                    if (dbEnabled) await deploymentService.setCaseSaving(client.id, true);
                    await deploymentService.updateBranding(client.id, branding);
                    if (tokenCost || deploymentCost) {
                      await deploymentService.setContract(client.id, { costPerToken: Number(tokenCost) || 50, oneTimeCost: Number(deploymentCost) || 0, currency: 'INR', billingCycle: 'monthly', notes: '' });
                    }
                    // Assign initial tokens
                    if (Number(initialTokens) > 0) {
                      await deploymentService.rechargeTokens(client.id, Number(initialTokens));
                    }
                    const result = await deploymentService.buildAndDownload(client.id, 'web');
                    if (result.success && result.downloadUrl) {
                      const bid = (result as any).buildId || '';
                      setBuildDone({ url: `${API}${result.downloadUrl}`, name: result.fileName || 'build.tar.gz', buildId: bid, clientId: client.id, credentials: (result as any).credentials });
                      // Auto-download
                      const a = document.createElement('a'); a.href = `${API}${result.downloadUrl}`; a.download = result.fileName || 'build.tar.gz'; a.click();
                      toast.success('Build complete! Source code downloaded.');
                    } else { toast.error(result.error || 'Build failed'); }
                  } catch (e: any) { toast.error(e.response?.data?.error || e.message); }
                  finally { setBuilding(false); }
                }} disabled={building || !clientInfo.name || !clientInfo.email}
                  className="w-full py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-2xl font-bold text-base hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-3 transition-all">
                  {building ? <><Loader2 size={20} className="animate-spin" /> Building...</> : <>📦 Build & Download</>}
                </button>

                {buildDone && (
                  <div className="p-5 bg-green-50 border border-green-200 rounded-xl space-y-3">
                    <div className="flex items-center gap-2">
                      <Check size={18} className="text-green-600" />
                      <span className="text-sm font-bold text-green-800">Build Complete!</span>
                    </div>
                    <div className="bg-white border border-green-200 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">🔐 Login Credentials:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-gray-500">Username:</span> <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-primary font-bold">{buildDone.credentials?.username || clientInfo.email.split('@')[0]}</code></div>
                        <div><span className="text-gray-500">Password:</span> <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-primary font-bold">{buildDone.credentials?.password || '—'}</code></div>
                      </div>
                    </div>
                    
                    {/* Platform Downloads */}
                    <div className="border-t border-green-200 pt-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">📥 Download Native App:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {(['android', 'windows', 'mac'] as const).map(plat => (
                          <button key={plat} onClick={async () => {
                            toast(`Building ${plat} app...`, { icon: '📦' });
                            try {
                              const r = await axios.post(`${API}/api/deployment/clients/${buildDone.clientId}/build-native/${plat}`, {}, {
                                headers: { 'x-superadmin-key': ADMIN_KEY },
                              });
                              const d = r.data;
                              if (d.success && d.downloadUrl) {
                                const a = document.createElement('a'); a.href = `${API}${d.downloadUrl}`; a.download = ''; a.click();
                                toast.success(`${plat} app ready!`);
                              } else { toast.error(d.error || 'Build failed'); }
                            } catch (e: any) { toast.error(e.response?.data?.error || e.message); }
                          }}
                            className="flex flex-col items-center gap-1 p-3 bg-white border border-gray-200 rounded-xl hover:border-primary hover:bg-indigo-50 transition-all text-center">
                            <span className="text-2xl">{plat === 'android' ? '📱' : plat === 'windows' ? '🪟' : '🍎'}</span>
                            <span className="text-[10px] font-semibold text-gray-700">{plat === 'android' ? 'Android' : plat === 'windows' ? 'Windows' : 'macOS'}</span>
                            <span className="text-[9px] text-gray-400">{plat === 'android' ? '.apk' : plat === 'windows' ? '.exe' : '.dmg'}</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-[9px] text-gray-400 text-center mt-2">Native apps store data locally on client's device. Only AI queries use the server.</p>
                    </div>

                    {/* Deploy to Cloud */}
                    <div className="border-t border-green-200 pt-3">
                      <button onClick={async () => {
                        if (!buildDone.clientId) { toast.error('Client ID missing'); return; }
                        toast('Deploying to cloud...', { icon: '☁️' });
                        try {
                          const r = await axios.post(`${API}/api/deployment/clients/${buildDone.clientId}/deploy-cloud`, {}, {
                            headers: { 'x-superadmin-key': ADMIN_KEY },
                          });
                          if (r.data.success) {
                            toast.success('Deployed to cloud!');
                            setBuildDone(prev => prev ? { ...prev, cloudUrl: r.data.url, cloudCname: r.data.cname, cloudSsl: r.data.ssl } : prev);
                          } else {
                            toast.error(r.data.error || 'Deploy failed');
                          }
                        } catch (e: any) { toast.error(e.response?.data?.error || e.message); }
                      }}
                        className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-bold text-sm hover:shadow-lg flex items-center justify-center gap-2 transition-all">
                        <Globe size={16} /> Deploy to Cloud (HTTPS)
                      </button>
                      {buildDone.cloudUrl && (
                        <div className="mt-3 space-y-2">
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs font-semibold text-blue-800 mb-1">🌐 Live URL (HTTPS):</p>
                            <a href={buildDone.cloudUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 font-mono underline break-all">{buildDone.cloudUrl}</a>
                            <div className="mt-2 flex items-center gap-2">
                              <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${buildDone.cloudSsl === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {buildDone.cloudSsl === 'active' ? '🔒 SSL Active' : '⏳ SSL Pending'}
                              </span>
                            </div>
                          </div>
                          {buildDone.cloudCname && (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <p className="text-xs font-semibold text-gray-700 mb-2">🔗 Custom Domain Setup (for client):</p>
                              <div className="bg-white border border-gray-300 rounded-lg p-2 font-mono text-[11px] text-gray-800">
                                <p className="text-gray-500 text-[9px] mb-1">Add this DNS record to use a custom domain:</p>
                                <p><span className="text-blue-600 font-bold">CNAME</span> &nbsp; app.clientfirm.com → <span className="text-green-700 font-bold">{buildDone.cloudCname.record}</span></p>
                              </div>
                              <p className="text-[9px] text-gray-500 mt-2">After DNS propagates, the client's custom domain will serve their app with HTTPS.</p>
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-[9px] text-gray-400 text-center mt-2">Each client gets: https://{'{clientId}'}.vidhiguru.in — clients can CNAME their own domain to it.</p>
                    </div>

                    <a href={buildDone.url} download className="block w-full py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 text-center">
                      ⬇️ Download Web Source Code ({buildDone.name})
                    </a>
                    <p className="text-[10px] text-gray-400 text-center">Source includes: server.js, public/, .env, DEPLOY.md, package.json{dbEnabled ? ', pb_schema.json, setup-wizard.js' : ''}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            {step > 1 ? <button onClick={() => setStep((step - 1) as Step)} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200"><ChevronLeft size={16} /> Back</button> : <div />}
            {step < 7 ? <button onClick={() => canNext() && setStep((step + 1) as Step)} disabled={!canNext()} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary to-secondary rounded-xl hover:shadow-lg disabled:opacity-50">Next <ChevronRight size={16} /></button> : <div />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeploymentStudioPage;
